import { HolidayTripData, HolidayTripDetail, HolidayTripDetailApi, HolidayTripPermissions } from 'api/holiday-trip-detail';
import { autoinject, computedFrom, newInstance } from 'aurelia-framework';
import { Redirect, RouteConfig, Router } from 'aurelia-router';
import { ValidationController } from 'aurelia-validation';
import { watch } from 'aurelia-watch-decorator';
import { DialogHelper } from 'framework/aurelia_dialogs/dialog-helper';
import { BootstrapGroupRenderer } from 'framework/aurelia_tools/bootstrap-group-renderer';
import { log } from 'framework/script_utilities/dev-decorators';
import { cloneDeep } from 'framework/script_utilities/object-equalizer';
import { BusinessFormType } from 'model/business-enums';
import { HolidayMaker, HolidayTrip, initHolidayMaker, initHolidayTrip, initRelatedTraveler } from 'model/forms/holiday-trip';
import { produceBaseSegments } from 'model/forms/trip-details';
import { IdentityService } from 'services/identity-service';
import { KeywordsService } from 'services/keywords-service';
import { LookupsService } from 'services/lookups-service';
import { requireServices } from 'support/activation';
import { FormSettings } from 'support/shared_models/form-settings';
import { ToastFormat, ToastHelper, ToastType } from 'support/toaster';
import { ValidationHelper } from 'support/validation';

@autoinject
export class HolidayTripDetailScreen {
  id: number = null;
  route: RouteConfig = null;
  model: HolidayTrip | HolidayTripData = null;
  permissions: HolidayTripPermissions = null;
  backup: HolidayTripDetail = null;
  loaded = false;
  editing = false;
  changed = false;

  constructor(private identity: IdentityService, private keywords: KeywordsService, private lookups: LookupsService, private api: HolidayTripDetailApi, private router: Router, private dialogHelper: DialogHelper, private toastHelper: ToastHelper, private validationHelper: ValidationHelper, @newInstance() private formSettings: FormSettings, @newInstance() private validationController: ValidationController, validationRenderer: BootstrapGroupRenderer) {
    formSettings.formType = BusinessFormType.HolidayTrip;
    validationRenderer.allowedParents = 'bs-form-input, bs-form-lookup, bs-form-select, bs-form-textarea';
    this.validationController.addRenderer(validationRenderer);
  }

  async canActivate() {
    await requireServices(this.identity, this.keywords, this.lookups);
  }

  @log('debug')
  async activate(params, route: RouteConfig) {
    this.id = parseInt(params?.id) || null;
    this.route = route;
  }

  bind() {
    this.initialize();
  }

  private async initialize() {
    this.bindDetail(this.id ? await this.api.getHolidayTrip(this.id) : null);
    this.editing = this.id && this.permissions.canEdit && this.autoEdit || !this.id;
    this.loaded = true;
  }

  async canDeactivate() {
    if (this.editing && this.changed && !await this.dialogHelper.confirm({ dialogTitle: 'Leave without saving?', message: 'You haven\'t saved the form yet. If you proceed, your changes will be lost!', okTitle: 'Leave without saving', cancelTitle: 'Stay here!' }))
      return new Redirect((this.router.history as any).previousLocation, { trigger: false, replace: false });
  }

  @log('debug')
  private bindDetail(detail?: HolidayTripDetail) {
    this.id = detail?.data.id ?? null;
    this.model = detail?.data ?? this.getEmptyForm();
    this.permissions = detail?.permissions ?? null;
    this.backup = cloneDeep(detail);
  }

  private get autoEdit() {
    return [(this.model as HolidayTripData)?.createdBy?.uniqueid, (this.model as HolidayTripData)?.modifiedBy?.uniqueid, (this.model as HolidayTripData)?.archivedBy?.uniqueid, ...this.model?.travelers.map(t => t.user?.uniqueid)].includes(this.identity.user.uniqueid);
  }

  editButtonClicked = false;
  editButtonVisible = false;
  archiveButtonVisible = false;
  restoreButtonVisible = false;
  submitButtonVisible = false;
  cancelButtonVisible = false;

  @watch((x: HolidayTripDetailScreen) => x.model)
  @watch((x: HolidayTripDetailScreen) => x.permissions)
  @watch((x: HolidayTripDetailScreen) => x.editing)
  @watch((x: HolidayTripDetailScreen) => x.changed)
  @watch((x: HolidayTripDetailScreen) => x.editButtonClicked)
  private updateButtons() {
    this.editButtonVisible = !this.editing && this.permissions?.canEdit;
    this.archiveButtonVisible = !(this.editing && this.changed) && this.permissions?.canArchive && (this.model as HolidayTripData)?.archivedOn == null;
    this.restoreButtonVisible = !(this.editing && this.changed) && this.permissions?.canRestore && (this.model as HolidayTripData)?.archivedOn != null;
    this.submitButtonVisible = this.editing && this.changed;
    this.cancelButtonVisible = this.editing && (this.changed || this.editButtonClicked);
  }

  edit() {
    this.editing = this.editButtonClicked = true;
  }

  async submit() {
    await this.validationHelper.validate(this.validationController) && await this.save();
  }

  async cancel() {
    if (!this.changed || await this.dialogHelper.confirm({ dialogTitle: 'Cancel your changes?', message: 'You haven\'t saved the form yet. If you proceed, your changes will be lost!', okTitle: 'Sure, do it!', cancelTitle: 'Back to form' })) {
      this.changed && this.bindDetail(this.backup);
      this.editing = !this.editButtonClicked;
    }
  }

  async archive() {
    this.bindDetail(await this.api.archiveHolidayTrip(this.id));
    this.toastHelper.toast('Your holiday trip has been successfully <b>archived</b>.', 'Archived', ToastType.Success, ToastFormat.Html);
    this.editing = this.editing && this.permissions.canEdit;
  }

  async restore() {
    this.bindDetail(await this.api.restoreHolidayTrip(this.id));
    this.toastHelper.toast('Your holiday trip has been successfully <b>restored</b>.', 'Restored', ToastType.Success, ToastFormat.Html);
    this.editing = this.permissions.canEdit && this.autoEdit;
  }

  private async save() {
    const data = this.getSaveData(this.model);
    this.bindDetail(this.id ? await this.api.updateHolidayTrip(this.id, data) : await this.api.createHolidayTrip(data));
    this.toastHelper.toast('Your holiday trip has been successfully <b>saved</b>.', 'Saved', ToastType.Success, ToastFormat.Html);
    this.editButtonClicked ? this.editing = false : this.router.navigateBack();
  }

  getSaveData(model: HolidayTrip): HolidayTrip {
    const data = cloneDeep(model);
    data.travelers.forEach(t => t.relatedTravelers = t.relatedTravelers.filter(r => r.name != null || r.relation != null));
    return data;
  }

  getEmptyForm(): HolidayTrip {
    return initHolidayTrip({
      travelers: [initHolidayMaker({ relatedTravelers: [initRelatedTraveler()] })],
      segments: produceBaseSegments(this.keywords.transportTypes[0] ?? null, this.keywords.getAccommodationTypes(this.formSettings.formType)[0] ?? null)
    });
  }

  @computedFrom('id', 'model')
  get title() {
    const getTravelerName = (t: HolidayMaker) => t.user == null ? null : t.user.name.split(' ')[0];
    return [this.id == null ? 'New' : this.model?.travelers.map(t => getTravelerName(t)).filter(n => n != null).map(n => `${n}'s`).join(' & '), 'Holiday Trip'].filter(p => p).join(' ');
  }

  @watch((x: HolidayTripDetailScreen) => x.model)
  private modelChanged() {
    this.route.navModel.setTitle(this.title);
    this.validationController.reset();
  }
}
